<template>
  <available-section
    class="report-services"
    section="reports"
  >
    <div class="report-services">
      <div class="report-services__header">
        <h1 class="report-services__title">Отчет по услугам</h1>
        <div class="report-services__form">
          <div class="report-services__row">
            <date-picker-dialog
              range
              v-bind:value="period"
              v-on:input="onInputPeriod"
              v-on:close="onCloseDatePickerDialog"
            >
              <template v-slot:trigger="{ listenerObject }">
                <button
                  type="button"
                  v-on="listenerObject"
                  class="report-services__search"
                >
                  <div v-if="period.length > 0">{{ periodTitle }}</div>
                  <div v-else>Выберите период</div>
                </button>
              </template>
            </date-picker-dialog>
            <button
              type="button"
              class="report-services__search__exit"
              v-if="period.length >= 1"
              v-on:click="period = []"
            />
          </div>
          <div class="report-services__tags">
            <button
              type="button"
              v-on:click="setPeriodByDay"
              class="report-services__tag"
              v-bind:class="{ active: periodName === $options.periodYesterday }"
            >Вчера</button>
            <button
              type="button"
              v-on:click="setPeriodByWeek"
              class="report-services__tag"
              v-bind:class="{ active: periodName === $options.periodWeek }"
            >Неделя</button>
            <button
              type="button"
              v-on:click="setPeriodByMonth"
              class="report-services__tag"
              v-bind:class="{ active: periodName === $options.periodMonth }"
            >Mесяц</button>
            <button
              type="button"
              v-on:click="setPeriodByYear"
              class="report-services__tag"
              v-bind:class="{ active: periodName === $options.periodYear }"
            >Год</button>
          </div>
          <the-select
            v-model="reportType"
            v-bind:options="$options.reportTypes"
            title="Тип отчета"
            class="report-services__select"
          />
          <button
            type="button"
            class="report-services__button-services"
            v-if="reportType === 'service'"
            v-on:click="showServiceDialog = true"
          >
            {{ chooseServicesButtonTitle }}
          </button>
          <div>
            <multiple-autocomplete-input
              v-if="reportType === 'employee'"
              v-model="selectedEmployees"
              chips
              item-text="name"
              item-value="id"
              label="Выберите сотрудников"
              multiple
              removal-per-chip
              select-all-text="Все сотрудники"
              set-select-all-option
              small-chips
              v-bind:items="employeeFilter" />
          </div>
          <choose-services-modal-dialog
            v-model="showServiceDialog"
            v-bind:items="servicesTree"
            v-on:change="onChangeServiceIds"
          />
        </div>
      </div>
      <div class="report-services__body">
        <pie-and-bar-diagram
          item-label-key="groupName"
          item-value-key="summary"
          v-bind:items="reportLinesSortedBySummary"
        />
        <button
          type="button"
          v-on:click="reportSpreadsheetFunction"
          class="report-services__button"
        >
          EXCEL
        </button>
        <report-table
          v-on:input="onChangeSort"
          v-bind:report-lines="reportLines"
          v-bind:need-clear-sorts="needClearSorts"
        />
      </div>
    </div>
  </available-section>
</template>

<script>

import DatePickerDialog from '@/components/DatePickerDialog.vue';
import TheSelect from '@/components/TheSelect.vue';
import notifierMixin from '@/mixins/notifierMixin';
import reportSalesMixin from '@/mixins/reportSalesMixin';
import PieAndBarDiagram from '@/components/PieAndBarDiagram.vue';
import ReportTable from '@/components/ReportTable.vue';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import ChooseServicesModalDialog from '@/components/ChooseServicesModalDialog.vue';
import debounce from 'lodash.debounce';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';

/**
 * @description Типы отчетов
 * @type {Array}
 */
const reportTypes = [
  { value: 'category', title: 'По отделениям' },
  { value: 'service', title: 'По услугам' },
  { value: 'employee', title: 'По сотрудникам' },
];

/**
 * @description Миксин отчета по услугам
 */
const ServicesReportMixin = {
  methods: {
    /**
     * @description Затянуть фильтр по услугам
     */
    fetchServiceFilter() {
      const onSuccess = (serviceGroups) => {
        this.servicesTree = serviceGroups.map((serviceGroup) => ({
          name: serviceGroup.group_name,
          id: serviceGroup.group_name,
          children: [...serviceGroup.services],
        }));
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      this
        .$reportGateway.getFilterForReportByServices()
        .then((either) => either.fold(onFail, onSuccess));
    },
    /**
     * @description Затянуть отчет
     */
    fetchServiceReport(type = '', direction = '') {
      const onSuccess = (lines) => {
        this.reportLines = lines;
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      if (
        this.period.length < 2
        || (this.reportType === 'service' && !this.serviceIds.length)
      ) {
        return;
      }
      if (type === '' || direction === '') {
        this.clearTableSorts();
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      const sort = { type, direction };
      if (this.reportType === 'service') {
        this
          .$reportGateway
          .getReportSalesServicesByServices(
            period,
            sort,
            this.serviceIds,
          )
          .then((either) => either.fold(onFail, onSuccess));
      } else if (this.reportType === 'category') {
        this
          .$reportGateway
          .getReportSalesServicesByCategories(period, sort)
          .then((either) => either.fold(onFail, onSuccess));
      }
    },
    /**
     * @description затянуть таблицу отчета
     */
    fetchServiceReportSpreadsheet() {
      const onSuccess = (file) => {
        this.saveFile(file, 'Отчет.xls');
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      if (
        this.period.length < 2
        || (this.reportType === 'service' && !this.serviceIds.length)
      ) {
        return;
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      if (this.reportType === 'service') {
        this
          .$reportGateway
          .getReportSalesServicesSheetByServices(period, this.serviceIds)
          .then((either) => either.fold(onFail, onSuccess));
      } else if (this.reportType === 'category') {
        this
          .$reportGateway
          .getReportSalesServicesSheetByCategories(period)
          .then((either) => either.fold(onFail, onSuccess));
      }
    },
  },
};

/**
 * @description Миксин отчета по сотрудникам
 */
const EmployeesReportMixin = {
  methods: {
    /**
     * @description Затянуть фильтр по сотрудникам.
     * @returns {Promise}
     */
    fetchEmployeeFilter() {
      const onSuccess = (filters) => {
        this.employeeFilter = filters.employees;
        this.selectedEmployees = this.employeeFilter.map(({ id }) => id);
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      const response = this.$reportGateway.getFilterForReportEmployeesSales();
      response.then((either) => either.fold(onFail, onSuccess));
      return response;
    },
    /**
     * @description Затянуть отчет
     */
    fetchEmployeeReport(type = '', direction = '') {
      const onSuccess = (lines) => {
        this.reportLines = lines;
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      if (
        this.period.length < 2
        || !this.selectedEmployees.length
      ) {
        return;
      }
      if (type === '' || direction === '') {
        this.clearTableSorts();
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      const sort = { type, direction };
      this
        .$reportGateway
        .getReportSalesEmployees(period, this.selectedEmployees, sort)
        .then((either) => either.fold(onFail, onSuccess));
    },
    /**
     * @description затянуть таблицу отчета
     */
    fetchEmployeeReportSpreadsheet() {
      const onSuccess = (file) => {
        this.saveFile(file, 'Отчет.xls');
      };
      const onFail = (error) => {
        this.notifyFail(error.message);
      };
      if (this.period.length < 2) {
        return;
      }
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      this
        .$reportGateway
        .getReportSalesEmployeesBySheet(period, this.selectedEmployees)
        .then((either) => either.fold(onFail, onSuccess));
    },
  },
};

export default {
  components: {
    MultipleAutocompleteInput,
    ChooseServicesModalDialog,
    AvailableSection,
    ReportTable,
    PieAndBarDiagram,
    TheSelect,
    DatePickerDialog,
  },
  computed: {
    /**
     * @description Заголовок кнопки выбора услуг
     * @return {string}
     */
    chooseServicesButtonTitle() {
      if (this.serviceIds.length) {
        return `Выбрано ${this.serviceIds.length}`;
      }
      return 'Выберите услуги';
    },
    /**
     * @description Строки отчета, отсортированные по "Итого"
     * @param reportLines
     * @return {*[]}
     */
    reportLinesSortedBySummary({ reportLines }) {
      const sortedReportLines = [...reportLines];
      return sortedReportLines.sort((line1, line2) => {
        if (line1.summary < line2.summary) {
          return -1;
        }
        if (line1.summary > line2.summary) {
          return 1;
        }
        return 0;
      });
    },
    /**
     * @description Функция отчета
     */
    reportFunction() {
      if (this.reportType === 'category' || this.reportType === 'service') {
        return this.fetchServiceReport;
      }
      return this.fetchEmployeeReport;
    },
    /**
     * @description Функция отчета таблицы
     */
    reportSpreadsheetFunction() {
      if (this.reportType === 'category' || this.reportType === 'service') {
        return this.fetchServiceReportSpreadsheet;
      }
      return this.fetchEmployeeReportSpreadsheet;
    },
    /**
     * @description Запрос.
     * @type {any}
     */
    viewQuery() {
      return {
        period: this.period,
        reportType: this.reportType,
      };
    },
  },
  created() {
    this.fetchServiceFilter();
    this.fetchEmployeeFilter();
    this.setPeriodByMonth();
  },
  data() {
    return {
      diagramType: 'pie',
      needClearSorts: false,
      reportLines: [],
      reportType: 'category',
      serviceIds: [],
      servicesTree: [],
      showDiagram: false,
      showServiceDialog: false,
      sortByDebt: '',
      sortByDiscount: '',
      sortByPaidAmount: '',
      sortBySummary: '',
      sortByTotalPrice: '',
      // по врачам
      employeeFilter: [],
      selectedEmployees: [],
    };
  },
  methods: {
    /**
     * @description Очистить сортировки таблицы
     */
    clearTableSorts() {
      this.needClearSorts = true;
      this.$nextTick(() => {
        this.needClearSorts = false;
      });
    },
    /**
     * Изменение услуг для выборки
     *
     * @param serviceIds
     */
    onChangeServiceIds(serviceIds) {
      this.serviceIds = serviceIds;
      this.reportFunction();
    },
    /**
     * @description Изменение сортировки
     * @param sort
     */
    onChangeSort(sort) {
      this.reportFunction(sort.type, sort.direction);
    },
  },
  mixins: [notifierMixin, reportSalesMixin, ServicesReportMixin, EmployeesReportMixin],
  watch: {
    viewQuery: debounce(function _() {
      this.reportFunction();
    }, 1000),
  },
  reportTypes,
  name: 'ClinicPanelReportSalesServices',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.v-dialog {
  width: 50%;
  .v-treeview-node__label {
    white-space: pre-wrap;
  }
  hr {
    padding: 0 0 10px 0;
  }
}
.report-services {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
    &__exit {
      width: 40px;
      height: 40px;
      border: 1px solid #C2CFE0;
      background-color: #EAEFF3;
      border-radius: 4px;
      background-image: url("@/assets/img/exit-dark-grey.svg");
      background-position: center;
      margin: 0 0 0 -2px;
    }
  }
  &__tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  }
  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    background: #EAEFF3;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    padding: 0 13px;
    &:hover,
    &.active {
      background: #0489E0;
      color: #FFFFFF;
    }
  }
  &__select {
    width: 400px;
  }
  &__body {
    padding: 22px 20px 100px 23px;
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    margin: 40px 0 30px auto;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
    &-services {
      @include buttons.btn-blue(155px, 40px, 16px);
    }
  }
}
@media screen and (max-width: 1200px) {
  .v-dialog {
    width: 80%;
  }
}

@media screen and (max-width: 480px) {
  .v-dialog {
    width: 99%;
  }
  .report-services {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
    &__button {
      width: 100%;
      &-services {
        width: 100%;
      }
    }
  }
}
</style>
